import { useRef, useState } from "react";
import { Table, Button } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import Select from "react-select";

import { toast } from "@/services/Utils";

import MultiSelect from "@/components/Form/MultiSelect/MultiSelect";
import CustomTooltip from "@/components/desktop/tooltip/CustomTooltip";
import LoadInventoryToolbar from "./LoadInventoryToolbar";
import FooterButtons from "../Buttons/FooterButtons";

import { ReactComponent as InformationIcon } from "@/assets/images/desktop/information.svg";

import { useInitialDataImplementation } from "@/hooks/implementation/firstLoad/initialDataImplementation";
import { useUsersBulkLoader } from "@/hooks/implementation/firstLoad/bulkLoader";

import "./rowsLoadStyle.scss";

const key = "initial_data_implementation";

const UsersTableLoad = (props) => {
	const { addToast } = useToasts();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const { customer } = useSelector((state) => state.auth.user);

	const { mutate, isLoading } = useUsersBulkLoader();

	const { data } = useInitialDataImplementation();
	const [isLockedDownload, setIsLockedDownload] = useState(false);

	const optionsEstateParents =
		data.estateParents &&
		data.estateParents.map((parent) => ({
			value: parent.value,
			label: parent.label,
		}));

	const optionsRoles =
		data.roles &&
		data.roles.map((role) => ({
			value: role.id,
			label: role.description,
		}));

	const fileInputRef = useRef(null);

	const addRow = () => {
		const newUser = [...data.users, { hash: uuidv4(), name: "", email: "", phone: "", estates_parents: [], estates: [], roles: [], isNew: true }];
		queryClient.setQueryData([key], { ...data, users: newUser });
	};

	const handleInputChange = (index, field, value) => {
		const newUser = data.users.map((row, i) => (i === index ? { ...row, [field]: value } : row));
		queryClient.setQueryData([key], { ...data, users: newUser });
	};

	const handleExportToExcel = () => {
		setIsLockedDownload(true);
		const filename = "PlantillaUsuarios.xlsx";
		const params = {};

		const payload = {
			file: {
				key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
				params: params,
				show: true,
				url: "/client/implementation/excel_users",
				name: filename,
				type: "excel",
				inExecution: false,
				inProgress: false,
				inChecked: false,
			},
		};

		new Promise((resolve, reject) => {
			try {
				dispatch({
					type: FETCHING_ADD_DOWNLOAD,
					payload: payload,
				});

				setTimeout(() => {
					resolve();
				}, 2000);
			} catch (error) {
				reject(error);
			}
		})
			.then(() => {
				setIsLockedDownload(false);
			})
			.catch(() => {
				setIsLockedDownload(false);
			});
	};

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;

		const reader = new FileReader();

		reader.onload = (e) => {
			const binaryStr = e.target.result;
			const workbook = XLSX.read(binaryStr, { type: "binary" });
			const firstSheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[firstSheetName];
			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			const updateUsers = [...data.users];

			const newRows = jsonData.map((item) => {
				const hash = item["CODIGO"] || uuidv4();
				const name = item["NOMBRE USUARIO"] || "";
				const email = item["EMAIL"] || "";
				const phone = String(item["TELEFONO"]) || "";

				const selectedParentEstates = item["TIPO CARGA"] === "TOTAL" ? optionsEstateParents : [];
				const selectedEstates =
					item["TIPO CARGA"] === "TOTAL"
						? data.estates &&
							data.estates.map((parent) => ({
								value: parent.id,
								label: parent.name,
							}))
						: [];

				const selectedRole = item["TIPO CARGA"] === "TOTAL" ? optionsRoles.find((or) => or.value === 7) : {};

				const user = data.users.find((user) => user.hash === hash) || {};
				const isNew = !updateUsers.some((user) => user.hash === hash);

				return {
					hash,
					name,
					email,
					phone,
					username: user.username || "",
					estates_parents: user.estates_parents || selectedParentEstates,
					estates: user.estates || selectedEstates,
					role: user.role || selectedRole,
					isNew,
				};
			});

			queryClient.setQueryData([key], { ...data, users: newRows });
		};

		reader.readAsBinaryString(file);

		fileInputRef.current.value = "";
	};

	const removeRow = (index, isNew) => {
		if (!isNew) {
			Swal.fire({
				title: "Confirmar eliminación",
				html: "Esta acción se completará al guardar o avanzar. ¿Deseas continuar?",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#7fb02f",
				cancelButtonColor: "#d33",
				confirmButtonText: "Sí, eliminar",
				cancelButtonText: "Cancelar",
				didOpen: () => {
					document.body.classList.remove("swal2-height-auto");
				},
			}).then((result) => {
				if (result.isConfirmed) {
					const newUser = data.users.filter((_, i) => i !== index);
					queryClient.setQueryData([key], { ...data, users: newUser });

					Swal.fire({
						title: "Eliminado",
						html: "El usuario ha sido eliminado",
						icon: "success",
						confirmButtonColor: "#7fb02f",
						confirmButtonText: "OK",
						didOpen: () => {
							document.body.classList.remove("swal2-height-auto");
						},
					});
				} else {
					toast(addToast, "Acción cancelada por el usuario");
				}
			});
		} else {
			const newUser = data.users.filter((_, i) => i !== index);
			queryClient.setQueryData([key], { ...data, users: newUser });
			toast(addToast, "Usuario eliminado");
		}
	};

	if (data?.users?.length === 0) {
		addRow();
	}

	const TextDescription = () => {
		return (
			<ul style={{ fontSize: "10px", padding: "10px", textAlign: "justify" }}>
				<li style={{ marginTop: "10px" }}>
					Acceso Básico: solo puede visualizar registros. No puede editar, crear, eliminar, ni aprobar nada. Tampoco puede ver precios.
				</li>
				<li style={{ marginTop: "10px" }}>Acceso Parcial: Puede visualizar, editar, crear, eliminar y aprobar. No puede ver los precios.</li>
				<li style={{ marginTop: "10px" }}>
					Acceso Total: Puede realizar todas las acciones de visualizar, editar, crear, eliminar y aprobar, incluyendo los precios
				</li>
			</ul>
		);
	};

	const validUsers = () => {
		let isValidUser = true;

		for (let i = 0; i < data?.users.length; i++) {
			const user = data.users[i];

			if (!user.name || user.name === "") {
				isValidUser = false;
				toast(addToast, `Se requiere que los usuarios tengan nombre en la fila ${i + 1}`, "error");
				break;
			}
			if (!user.email || user.email === "") {
				isValidUser = false;
				toast(addToast, `Se requiere que los usuarios tengan email en la fila ${i + 1}`, "error");
				break;
			}
			if (!user.phone || user.phone === "") {
				isValidUser = false;
				toast(addToast, `Se requiere que los usuarios tengan teléfono en la fila ${i + 1}`, "error");
				break;
			}
			if (user.phone && user.phone !== "" && !isPossiblePhoneNumber(`+${user.phone.replace(/\+/g, "")}`)) {
				isValidUser = false;
				toast(addToast, `El teléfono debe tener 9 dígitos en la fila ${i + 1}`, "error");
				break;
			}
			if (customer.use_estates_parents && user.estates_parents.length === 0) {
				isValidUser = false;
				toast(addToast, `Se requiere que los usuarios tengan campos en la fila ${i + 1}`, "error");
				break;
			}
			if (user.estates.length === 0) {
				isValidUser = false;
				toast(addToast, `Se requiere que los usuarios tengan bodegas en la fila ${i + 1}`, "error");
				break;
			}
		}

		return isValidUser;
	};

	const saveUsers = ({ moveForward = false }) => {
		if (validUsers()) {
			const queryData = { ...queryClient.getQueryData([key]) };
			queryData.implementationStep = moveForward ? data.implementationStep + 1 : data.implementationStep;

			mutate(queryData, {
				onSuccess: (response) => {
					if (Object.prototype.hasOwnProperty.call(response, "users")) {
						toast(addToast, response.message, "success");

						queryClient.setQueryData([key], {
							...data,
							users: response.users,
							implementationStep: response.implementationStep,
						});

						if (props.formWizardRef.current?.nextTab && moveForward) {
							props.formWizardRef.current.nextTab();
						}
					}
				},
			});
		}
	};

	return (
		<>
			<LoadInventoryToolbar
				handleExportToExcel={handleExportToExcel}
				handleFileUpload={handleFileUpload}
				isLockedDownload={isLockedDownload}
				fileInputRef={fileInputRef}
			/>
			<Table hover className="table-user">
				<thead>
					<tr>
						<th className="border-0">
							Nombre <small className="text-danger">*</small>
						</th>
						<th className="border-0">
							Email <small className="text-danger">*</small>
						</th>
						<th className="border-0">
							<CustomTooltip
								text={
									<div>
										<InformationIcon style={{ width: "1em", height: "1em" }} /> Teléfono <small className="text-danger">*</small>
									</div>
								}
								description={"Formato 9 dígitos, ej: 912345678"}
							/>
						</th>
						{!!customer.use_estates_parents && (
							<th className="border-0">
								Campos <small className="text-danger">*</small>
							</th>
						)}
						<th className="border-0">
							Bodegas <small className="text-danger">*</small>
						</th>
						<th className="border-0">
							<CustomTooltip
								text={
									<div>
										<InformationIcon style={{ width: "1em", height: "1em" }} /> Rol <small className="text-danger">*</small>
									</div>
								}
								description={<TextDescription />}
							/>
						</th>
						<th className="border-0 text-center">Acciones</th>
					</tr>
				</thead>
				<tbody>
					{data?.users?.map((row, index) => (
						<tr key={row.hash} className="userRow">
							<td className="position-relative vertical-align">
								<input
									type="text"
									className="form-control customInput"
									placeholder="Nombre de usuario"
									required
									defaultValue={row.name || ""}
									onBlur={(e) => handleInputChange(index, "name", e.target.value)}
								/>
								{row.isNew && (
									<div className="diagonal-badge">
										<span className="badge badge-info">Nuevo</span>
									</div>
								)}
								{row.username === data.currentUser && (
									<div className="diagonal-badge">
										<span className="badge" style={{ fontSize: "8px", backgroundColor: "#7fb02f" }}>
											<small>
												Mi <br />
												usuario
											</small>
										</span>
									</div>
								)}
							</td>
							<td className="position-relative vertical-align">
								<input
									type="email"
									className="form-control customInput"
									placeholder="Email de usuario"
									required
									defaultValue={row.email || ""}
									onBlur={(e) => handleInputChange(index, "email", e.target.value)}
								/>
							</td>
							<td className="position-relative vertical-align" style={{ width: "10%" }}>
								<PhoneInput
									initialValueFormat="national"
									style={{ fontSize: "12px" }}
									defaultCountry="CL"
									value={row && row.phone ? `+${row.phone.replace(/\+/g, "")}` : ""}
									onChange={(value) => handleInputChange(index, "phone", value)}
									type="text"
								/>
							</td>
							{!!customer.use_estates_parents && (
								<td className="parentStatesColumnUserSelector vertical-align">
									<MultiSelect
										key="estate_parent_selector"
										options={optionsEstateParents}
										onChange={(e) => handleInputChange(index, "estates_parents", e)}
										value={row.estates_parents}
										isSelectAll={true}
										menuPlacement={"bottom"}
										placeholder={"Selecciona campos"}
									/>
								</td>
							)}
							<td className="estatesColumnUserSelector vertical-align">
								<MultiSelect
									key="estate_selector"
									options={
										customer.use_estates_parents
											? data.estates
													.filter((estate) => row.estates_parents.some((ep) => ep.value === estate.estate_parent_id))
													.map((est) => ({ value: est.id, label: est.name }))
											: data.estates.map((est) => ({ value: est.id, label: est.name }))
									}
									onChange={(e) => handleInputChange(index, "estates", e)}
									value={row.estates}
									isSelectAll={true}
									menuPlacement={"bottom"}
									placeholder={"Selecciona campos"}
								/>
							</td>
							<td className="rolesColumnUserSelector vertical-align">
								<Select
									key="roles"
									options={data.roles && data.roles.map((role) => ({ value: role.id, label: role.description }))}
									onChange={(e) => handleInputChange(index, "role", e)}
									value={row.role}
									menuPlacement={"bottom"}
									placeholder={"Selecciona roles"}
								/>
							</td>
							<td width={"5%"} className="text-center vertical-align">
								<div className="btn-group">
									{data.currentUser !== row.username && (
										<Button variant="danger" onClick={() => removeRow(index, row.isNew)}>
											<i className="ti-trash"></i>
										</Button>
									)}
								</div>
							</td>
						</tr>
					))}
					<tr>
						<td className="border-0">
							<button className="btn bg-agro-outline-color btn-block" onClick={addRow} style={{ height: "30px", fontSize: "12px" }}>
								<i className="ti-plus"></i> Agregar
							</button>
						</td>
					</tr>
				</tbody>
			</Table>

			<FooterButtons wizard={props.formWizardRef} currentIndex={props.index} handleSave={saveUsers} isLoading={isLoading} />
		</>
	);
};

export default UsersTableLoad;
